import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { media, styled } from '@nx-kit/styling';
import { COURSE_DOMAIN, DEFAULT_DOMAIN, GUIDE_BASE } from 'prismic/config';
import { DomainContext } from 'contexts/domain/DomainContext';

export const BgMotivStyled = styled.div`
  position: absolute;
  height: 500px;
  width: 100%;
  z-index: -1;

  img {
    position: absolute;
    right: -100px;
    top: 0;
    height: 500px;
    ${media('md')} {
      right: 0;
      height: 700px;
    }
  }
`;

const SingleImageStyled = styled.div`
  position: absolute;
  top: 100px;
  width: 100%;
  height: 900px;
  display: flex;
  flex-direction: column;
  background-repeat: no-repeat;
  background-size: auto 630px;

  ${media('md')} {
    background-position: 80% 60px;
    background-size: auto 720px;
  }

  ${media('lg')} {
    background-size: cover;
    background-position: center;
  }
`;

// Same footprint as the homepage hero background image, but as a looping video.
// A semi-transparent white overlay (::after) lightens the dark footage so the
// hero text stays readable — "more white, less black".
const SingleVideoWrapper = styled.div`
  position: absolute;
  top: 100px;
  left: 0;
  width: 100%;
  height: 720px;
  overflow: hidden;

  ${media('md')} {
    height: 820px;
  }

  ${media('lg')} {
    height: 1000px;
  }

  /* Full-width even dark overlay (client request: the overlay must cover the
     whole hero, not just the left half). */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.42);
  }
`;

// Full-colour image film (Brienz-style).
const SingleVideoStyled = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

// Ballenberg's image film (full colour), in the two formats it was cut in:
// 16:9 landscape for desktop, 9:16 portrait for phones. The portrait cut on a
// wide hero crops down to a narrow strip, so the source is picked per viewport.
// TODO: both files are large — compress to web-optimized versions (a few MB)
// before deploying to production.
const HERO_VIDEO_DESKTOP = '/videos/hero-ballenberg.mp4';
const HERO_VIDEO_MOBILE = '/videos/Ballenberg_Allgemein_9-16.mp4';
// Matches the theme's `md` breakpoint (min 768px).
const MOBILE_QUERY = '(max-width: 767.98px)';

const BGImage: React.FC = () => {
  const { asPath } = useRouter();
  const {
    computed: { isCourse },
  } = useContext(DomainContext);

  // Picked on the client only: rendering a <source> during SSR would either
  // mismatch on hydration or make every visitor download the wrong cut. Until
  // it resolves, the poster stands in.
  const [heroVideo, setHeroVideo] = useState<string | null>(null);

  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const apply = () => setHeroVideo(query.matches ? HERO_VIDEO_MOBILE : HERO_VIDEO_DESKTOP);

    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);

  const isMainHomepage = asPath === '/' || asPath === `/${DEFAULT_DOMAIN}`;

  // Homepage hero: show a looping background video instead of the static image
  // (sample for now — Ballenberg will deliver the final video).
  if (isMainHomepage && !isCourse) {
    return (
      <SingleVideoWrapper data-type="motiv-video">
        <SingleVideoStyled
          // Remount when the cut changes so the browser actually reloads it.
          key={heroVideo ?? 'poster'}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/videos/hero-ballenberg-poster.jpg"
        >
          {heroVideo && <source src={heroVideo} type="video/mp4" />}
        </SingleVideoStyled>
      </SingleVideoWrapper>
    );
  }

  if (
    asPath === '/' ||
    asPath === `/${DEFAULT_DOMAIN}` ||
    asPath === `/${COURSE_DOMAIN}` ||
    asPath === `/${GUIDE_BASE}`
  ) {
    return (
      <SingleImageStyled
        data-type="motiv"
        style={{
          backgroundImage: isCourse
            ? "url('/motivs/scherenschnitte_kurse.jpg')"
            : "url('/motivs/scherenschnitte_hero_home.jpg')",
        }}
      />
    );
  }

  return null;
};

export default BGImage;
